import { useCallback, useMemo } from 'react'
import { View } from 'react-native'
import styled, { useTheme } from 'styled-components'
import { FadingArrowCarousel } from '../../../elements/carousels/FadingArrowCarousel/FadingArrowCarousel'
import { ComponentError, ComponentPlaceholder } from '../../../elements/status'
import statusMsgs from '../../../elements/status/messages'
import { useDimensions } from '../../../hooks/index.js'
import { media, useMediaSelect } from '../../../style/media'
import { PosterContent as PosterPortraitContent } from '../../content-lists/PosterContent/PosterContent'
import { ListContainer } from '../ListContainer'
import { ListTitle } from '../ListTitle/ListTitle'

const responsive = {
  lg: { slidesToShow: 10, slidesToScroll: 10, containerFactor: 1 },
  md: { slidesToShow: 6, slidesToScroll: 6, containerFactor: 1 },
  sm: { slidesToShow: 4, slidesToScroll: 4, containerFactor: 1 },
  xs: { slidesToShow: 4, slidesToScroll: 3, containerFactor: 1.22 },
}

const OverflowBounds = styled(View)`
  overflow: hidden;
  width: 100%;
`

const OverflowContainer = styled(View)`
  width: 122%;
  ${media.sm`
    width: auto;
  `};
`

export const PosterContent = ({
  slug,
  title,
  loading,
  error,
  items,
  onItemChosen,
  showArrows,
  itemProps,
  ...props
}) => {
  const theme = useTheme()
  const { width } = useDimensions()

  const { slidesToShow, containerFactor } = useMediaSelect(responsive)
  const containerHeight = PosterPortraitContent.calcHeight(
    FadingArrowCarousel.availableWidth(width * containerFactor) / slidesToShow,
    theme,
  )

  // The last item is only partially visible; without a blank at the end the
  // last item will always be stuck off-screen.
  const itemsWithBlanks = useMemo(() => [...(items ?? []), null], [items])

  const renderItem = useCallback(
    (item, _, width) =>
      item ? (
        <PosterPortraitContent
          {...itemProps}
          item={item}
          width={width}
          onChosen={onItemChosen}
          theme={theme}
        />
      ) : (
        <View style={{ width, height: PosterPortraitContent.calcHeight(width, theme) }} />
      ),
    [theme, onItemChosen],
  )

  let content
  if (loading) {
    content = <ComponentPlaceholder height={containerHeight} background="grayDark" />
  } else if (error) {
    content = (
      <ComponentError horizontal="left" height={containerHeight} {...statusMsgs.listLoadError} />
    )
  } else {
    content = (
      <OverflowContainer>
        <FadingArrowCarousel
          {...props}
          gradientOpacity={0.5}
          height={containerHeight}
          items={itemsWithBlanks}
          scrollPositionSlug={slug}
          responsive={responsive}
          renderItem={renderItem}
          showPrev={showArrows}
          showNext={showArrows}
          // A blank is added at the end so all films can scroll into view;
          // making the scroll infinite would allow scrolling to the gap.
          infinite={false}
        />
      </OverflowContainer>
    )
  }

  return (
    <ListContainer>
      <ListTitle>{title || ' '}</ListTitle>
      <OverflowBounds>{content}</OverflowBounds>
    </ListContainer>
  )
}
